import { computed, ref, type Ref } from 'vue';

import { GlobalState } from '@/global';
import { OtelState } from '@/otel';

import { Logger } from './logger';

const log = Logger.createScoped('extension/c2c');

// Extension message types
interface ExtensionMessage {
    source: 'zyno-extension';
    type: string;
    [key: string]: unknown;
}

interface ExtensionReadyMessage extends ExtensionMessage {
    type: 'EXTENSION_READY';
    extensionId: string;
    preferredTenantId: string | null;
}

interface DialNumberMessage extends ExtensionMessage {
    type: 'DIAL_NUMBER';
    phoneNumber: string;
}

interface PreferredTenantChangedMessage extends ExtensionMessage {
    type: 'PREFERRED_TENANT_CHANGED';
    tenantId: string;
}

// State
const isExtensionInstalled = ref(false);
const preferredTenantId = ref<string | null>(null);
const matchesTenant = computed(() => {
    const currentTenantId = GlobalState.tenant.value?.id;
    return isExtensionInstalled.value && preferredTenantId.value === currentTenantId;
});
let dialHandler: ((phoneNumber: string) => void) | null = null;
let isInCallRef: Ref<boolean> | null = null;
let initCount = 0;

function sendToExtension(message: Record<string, unknown>) {
    log.debug('SPA -> Extension', message);
    window.postMessage({ source: 'zyno-spa', ...message }, '*');
}

function handleExtensionMessage(event: MessageEvent) {
    if (event.source !== window) return;
    const data = event.data as ExtensionMessage;
    if (!data || data.source !== 'zyno-extension') return;

    log.debug('Extension -> SPA', data);

    switch (data.type) {
        case 'EXTENSION_READY': {
            const msg = data as ExtensionReadyMessage;
            isExtensionInstalled.value = true;
            preferredTenantId.value = msg.preferredTenantId;
            break;
        }

        case 'IDENTIFY': {
            // Extension is asking for our current state
            const tenantId = GlobalState.tenant.value?.id;
            if (tenantId) {
                sendToExtension({
                    type: 'IDENTIFY_RESPONSE',
                    tenantId,
                    isInCall: isInCallRef?.value ?? false,
                    workstationId: OtelState.workstationId
                });
            }
            break;
        }

        case 'DIAL_NUMBER': {
            const msg = data as DialNumberMessage;
            if (dialHandler) {
                dialHandler(msg.phoneNumber);
            }
            break;
        }

        case 'PREFERRED_TENANT_CHANGED': {
            const msg = data as PreferredTenantChangedMessage;
            preferredTenantId.value = msg.tenantId;
            break;
        }
    }
}

export const ClickToCallExtension = {
    /** Reactive ref indicating if extension is installed and configured for this tenant */
    matchesTenant,

    /** Check if extension is installed */
    isWorkstationed() {
        return isExtensionInstalled.value;
    },

    /** Get the Chrome Web Store URL for the extension */
    getInstallUrl() {
        if (navigator.userAgent.includes('Edg/')) {
            return 'https://s24.io/zyno-extension-edge';
        } else {
            return 'https://s24.io/zyno-extension-chrome';
        }
    },

    /** Store the current tenant ID in the extension */
    setPreferredTenant() {
        const tenantId = GlobalState.tenant.value?.id;
        if (!tenantId) return;

        sendToExtension({ type: 'SET_PREFERRED_TENANT', tenantId });
        preferredTenantId.value = tenantId;
    },

    /**
     * Initialize the click-to-call extension integration
     * @param onDial Callback when a phone number should be dialed
     * @param isInCall Ref indicating if there's an active call
     */
    init(onDial: (phoneNumber: string) => void, isInCall: Ref<boolean>) {
        initCount++;

        if (dialHandler) return; // Already initialized

        dialHandler = onDial;
        isInCallRef = isInCall;

        // Listen for extension messages
        window.addEventListener('message', handleExtensionMessage);

        // Send a ping to check if extension is already loaded
        // The bridge script sends EXTENSION_READY on load, but we might have loaded after it
        setTimeout(() => {
            if (!isExtensionInstalled.value) {
                // Try to detect extension by checking for bridge
                // Include tenantId so extension can set it as preferred if it doesn't have one
                sendToExtension({ type: 'HELLO', tenantId: GlobalState.tenant.value?.id });
            }
        }, 100);
    },

    /** Clean up extension integration */
    cleanup() {
        initCount--;

        // Only cleanup when all init cycles are complete
        if (initCount > 0) return;

        window.removeEventListener('message', handleExtensionMessage);

        dialHandler = null;
        isInCallRef = null;
    }
};
